import { motion } from "framer-motion";
import { useMemo } from "react";
import type { ExhibitImage } from "@/lib/api";
import { colors, fonts, shadows, layout, spacing } from "@/lib/theme";

const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 24 24' fill='none' stroke='%23ccc' stroke-width='0.5'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='M21 15l-5-5L5 21'/%3E%3C/svg%3E";

interface DisplayPieceProps {
  image: ExhibitImage;
}

/**
 * Individual piece display with image and optional title/caption
 * Mobile: All images centered with text below
 * Desktop layout varies by aspect ratio:
 * - Wide images: vertical stack with text below
 * - Tall/Square images: side-by-side (text left or right, randomized)
 */
export function DisplayPiece({ image }: DisplayPieceProps) {
  // Randomize text position on desktop (left/right of image)
  const textOnRight = useMemo(() => Math.random() > 0.5, []);

  const hasText = image.title || image.caption;

  // Animation config for scroll reveal
  const containerAnimation = {
    initial: { opacity: 0, y: 80 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: false, amount: 0.3 },
    transition: { duration: 0.7, ease: "easeOut" as const },
  };

  const textAnimation = {
    initial: { opacity: 0 },
    whileInView: { opacity: 1 },
    viewport: { once: false, amount: 0.5 },
    transition: { duration: 0.5, delay: 0.2 },
  };

  // All images: full viewport height, centered
  return (
    <motion.div
      {...containerAnimation}
      className="min-h-screen flex flex-col items-center justify-center px-4 md:px-6"
    >
      {image.aspectRatio === "wide" ? (
        // Wide images: vertical stack
        <div className="flex flex-col items-center">
          <PieceImage
            src={image.imagePath}
            alt={image.title || ""}
            maxHeight={layout.imageMaxHeight.wide}
          />

          {hasText && (
            <motion.div
              {...textAnimation}
              className="mt-6 text-center max-w-2xl mx-auto"
            >
              <PieceText title={image.title} caption={image.caption} centered />
            </motion.div>
          )}
        </div>
      ) : (
        // Tall or Square images: centered on mobile, side-by-side on desktop
        <div
          className={`flex flex-col items-center gap-6 md:gap-8 md:flex-row max-w-5xl ${
            textOnRight ? "" : "md:flex-row-reverse"
          }`}
        >
          <div className={`${hasText ? "md:w-3/5" : "w-full"} flex justify-center`}>
            <PieceImage
              src={image.imagePath}
              alt={image.title || ""}
              maxHeight={layout.imageMaxHeight.tall}
            />
          </div>

          {hasText && (
            <motion.div {...textAnimation} className="md:w-2/5 text-center md:text-left">
              <PieceText title={image.title} caption={image.caption} />
            </motion.div>
          )}
        </div>
      )}
    </motion.div>
  );
}

/**
 * Image component with consistent styling
 */
interface PieceImageProps {
  src: string;
  alt: string;
  maxHeight: string;
}

function PieceImage({ src, alt, maxHeight }: PieceImageProps) {
  return (
    <img
      src={src}
      alt={alt}
      className="rounded"
      style={{
        maxHeight,
        maxWidth: "100%",
        objectFit: "contain",
        boxShadow: shadows.image,
        backgroundColor: colors.border,
      }}
      onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }}
    />
  );
}

/**
 * Text component for title and caption
 */
interface PieceTextProps {
  title?: string | null;
  caption?: string | null;
  centered?: boolean;
}

function PieceText({ title, caption, centered }: PieceTextProps) {
  return (
    <>
      {title && (
        <h3
          style={{
            fontFamily: fonts.serif,
            fontSize: "1.5rem",
            color: colors.text,
            marginBottom: caption ? "12px" : "0",
          }}
        >
          {title}
        </h3>
      )}
      {caption && (
        <p
          style={{
            fontFamily: fonts.sans,
            fontSize: "1rem",
            color: colors.textMuted,
            lineHeight: centered ? "1.6" : "1.7",
          }}
        >
          {caption}
        </p>
      )}
    </>
  );
}
